// v2.0.2 — Card de navigation vers une adresse CIV-AI dans le chat.
// Affichée quand un utilisateur tape "Conduit moi à CIV-XXX-YYYY-NNN".
// v2.15.1 — Ajout : badge "cities_registry_match" (résolution approximative) +
//           bouton ⭐ Favori (POST /api/user/favorites) avec mini-popup nom + checkbox /mon-compte.

import { useState } from "react";
import axios from "axios";
import { MapContainer, TileLayer, Marker } from "react-leaflet";
import { toast } from "sonner";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import useUserId from "../hooks/useUserId";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const RESOLUTION_BADGE = {
  exact_commerce_match: { label: "🏪 Commerce identifié", color: "#16a34a" },
  registry_match: { label: "📍 Adresse retrouvée", color: "#2563eb" },
  street_only: { label: "🛣 Rue approximative", color: "#ca8a04" },
  cities_registry_match: { label: "🎯 Position approximative", color: "#a855f7" },
  geocodec_decoded: { label: "📐 Décodé précisément", color: "#06B6D4" },
  invalid: { label: "⚠️ Code invalide", color: "#dc2626" },
};

export default function AddressNavigateCard({ data }) {
  const [copied, setCopied] = useState(false);
  const [showFavPopup, setShowFavPopup] = useState(false);
  const [favName, setFavName] = useState("");
  const [alsoAddToAccount, setAlsoAddToAccount] = useState(false);
  const [savingFav, setSavingFav] = useState(false);
  const [favSaved, setFavSaved] = useState(false);
  const userId = useUserId();
  if (!data) return null;
  const { code, commune, city, street_name, lat, lng, distance_m, warning, resolution, navigate_url, commerce_match, precision } = data;
  const isInvalid = resolution === "invalid" || warning === "distance_aberrante";
  const hasCoords = typeof lat === "number" && typeof lng === "number";
  const badge = RESOLUTION_BADGE[resolution] || RESOLUTION_BADGE.invalid;
  const placeLabel = street_name || commune || city || code;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      toast.success("Code copié ✓");
      setTimeout(() => setCopied(false), 2000);
    } catch (_) {
      toast.error("Copie impossible");
    }
  };

  const openDelivery = () => {
    if (navigate_url) window.location.href = navigate_url;
  };

  // v2.15.1 — Sauvegarde en favori (+ option : ajouter à Mon Compte).
  const openFavPopup = () => {
    setFavName(placeLabel || code);
    setShowFavPopup(true);
  };
  const saveFavorite = async () => {
    if (!favName.trim()) { toast.error("Donne un nom à ton favori"); return; }
    if (!hasCoords) { toast.error("Coordonnées manquantes"); return; }
    setSavingFav(true);
    try {
      const r = await axios.post(`${API}/user/favorites`, {
        user_id: userId,
        name: favName.trim(),
        address_civ: code,
        lat, lng,
        source: "chat",
      });
      if (!r.data?.ok) throw new Error(r.data?.reason || "fav_failed");
      toast.success(`⭐ ${favName.trim()} ajouté à tes favoris`);
      setFavSaved(true);

      // Option : ajouter aussi à Mon Compte (sections "autres adresses" v3).
      if (alsoAddToAccount) {
        // Best-effort : on tente de générer un suffix v3 puis créer l'adresse.
        // En cas d'échec (ville/commune inconnues du registry v3), on ne casse pas le flux favori.
        try {
          const m = code.match(/CIV-([A-Z]{2,4})-([A-Z]{2,4})-([A-Z0-9-]+)/i);
          if (m) {
            const cityCode = m[1].toUpperCase();
            const communeCode = m[2].toUpperCase();
            const suffixRaw = m[3].toUpperCase().replace(/[^A-Z0-9]/g, '').slice(-5);
            const isValidSuffix = suffixRaw.length === 5 && /\d/.test(suffixRaw) && /[A-Z]/.test(suffixRaw);
            const finalSuffix = isValidSuffix
              ? suffixRaw
              : (await axios.post(`${API}/v3/addresses/generate-suffix`, { city_code: cityCode, commune_code: communeCode })).data.suffix;
            await axios.post(`${API}/v3/addresses`, {
              user_id: userId,
              city_code: cityCode,
              commune_code: communeCode,
              suffix: finalSuffix,
              name: favName.trim(),
              description: `Depuis chat — ${code}`,
              lat, lng,
              is_home: false,
            });
            toast.success("📌 Ajouté aussi à Mon Compte");
          }
        } catch (eAcc) {
          toast.message(`Favori OK, mais ajout à Mon Compte impossible : ${eAcc.response?.data?.reason || eAcc.message}`);
        }
      }
      setShowFavPopup(false);
    } catch (e) {
      toast.error(e.response?.data?.reason || e.message);
    } finally {
      setSavingFav(false);
    }
  };

  return (
    <div className="civ-navigate-card" data-testid="address-navigate-card">
      <div className="civ-navigate-head">
        <span className="civ-navigate-pin">🛵</span>
        <h3 className="civ-navigate-title">Navigation CIV-AI</h3>
        <span className="civ-navigate-badge" style={{ background: badge.color }} data-testid="address-navigate-badge">
          {badge.label}
        </span>
      </div>
      <div className="civ-navigate-code" data-testid="address-navigate-code">{code}</div>
      {precision === "approximate" && !isInvalid && (
        <div data-testid="address-navigate-precision-note" style={{ fontSize: 11, color: "#a855f7", margin: "2px 0 6px" }}>
          🎯 Position approximative (centroïde commune + offset déterministe)
        </div>
      )}
      {!isInvalid && (street_name || commune) && (
        <div className="civ-navigate-addr">
          📍 <strong>{street_name || "Rue inconnue"}</strong>{commune ? `, ${commune}` : ""}
          {typeof distance_m === "number" && <span className="civ-navigate-dist"> · à {distance_m}m du carrefour repère</span>}
        </div>
      )}
      {isInvalid && (
        <div className="civ-navigate-invalid" data-testid="address-navigate-invalid">
          Ce code semble invalide (distance &gt; 50 km du carrefour de référence, ce qui est impossible pour Abidjan).
          Vérifie la saisie du code — il devrait suivre le format <code>CIV-COM-RUE-NNN-DDD</code> avec une distance &lt; 50 000.
        </div>
      )}
      {commerce_match && (
        <div className="civ-navigate-commerce" data-testid="address-navigate-commerce">
          <strong>{commerce_match.nom}</strong> — {commerce_match.categorie}
          {commerce_match.contact && <span> · 📞 {commerce_match.contact}</span>}
        </div>
      )}
      {hasCoords && !isInvalid && (
        <div className="civ-navigate-map" data-testid="address-navigate-map">
          <MapContainer
            center={[lat, lng]}
            zoom={16}
            scrollWheelZoom={false}
            dragging={false}
            style={{ width: "100%", height: 180, borderRadius: 8 }}
            attributionControl={false}
          >
            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="&copy; OpenStreetMap" />
            <Marker position={[lat, lng]} />
          </MapContainer>
        </div>
      )}
      <div className="civ-navigate-actions">
        {!isInvalid && navigate_url && (
          <button
            type="button"
            className="civ-navigate-go-btn"
            onClick={openDelivery}
            data-testid="open-livreur-btn"
          >
            🛵 Ouvrir le navigateur
          </button>
        )}
        {!isInvalid && hasCoords && (
          <button
            type="button"
            className="civ-navigate-copy-btn"
            onClick={openFavPopup}
            disabled={favSaved}
            data-testid="save-favorite-btn"
            style={{
              background: favSaved ? "rgba(34,197,94,0.18)" : "rgba(245,158,11,0.18)",
              color: favSaved ? "#86EFAC" : "#FBBF24",
              border: `1px solid ${favSaved ? "#22C55E" : "#F59E0B"}`,
            }}
          >
            {favSaved ? "⭐ Enregistré" : "⭐ Favori"}
          </button>
        )}
        <button
          type="button"
          className="civ-navigate-copy-btn"
          onClick={copy}
          data-testid="address-navigate-copy-btn"
        >
          {copied ? "✓ Copié" : "📋 Copier"}
        </button>
        {!isInvalid && hasCoords && (
          <a
            className="civ-navigate-map-btn"
            href={`/addressing?focus=${encodeURIComponent(code)}&lat=${lat}&lng=${lng}`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="address-navigate-map-link"
          >
            🗺 Voir sur la carte ↗
          </a>
        )}
      </div>

      {showFavPopup && hasCoords && (
        <div data-testid="favorite-popup" style={{
          marginTop: 10, padding: "10px 12px", background: "rgba(15,23,42,0.97)",
          border: "1px solid rgba(245,158,11,0.3)", borderRadius: 8, display: "flex", flexDirection: "column", gap: 6,
        }}>
          <label style={{ fontSize: 11, color: "#94A3B8" }}>Nom du favori</label>
          <input
            type="text"
            data-testid="favorite-name-input"
            value={favName}
            onChange={(e) => setFavName(e.target.value)}
            placeholder="Ex: Sococé"
            style={{ padding: 6, background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.12)", color: "#fff", borderRadius: 4, fontSize: 12 }}
          />
          <label style={{ fontSize: 11, color: "#CBD5E1", display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
            <input type="checkbox" data-testid="favorite-also-account" checked={alsoAddToAccount} onChange={(e) => setAlsoAddToAccount(e.target.checked)} />
            Ajouter aussi à <strong>Mon Compte</strong>
          </label>
          <div style={{ display: "flex", gap: 6, justifyContent: "flex-end", marginTop: 4 }}>
            <button type="button" onClick={() => setShowFavPopup(false)} style={{ padding: "6px 10px", background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.12)", color: "#CBD5E1", borderRadius: 4, cursor: "pointer", fontSize: 11 }}>Annuler</button>
            <button type="button" data-testid="favorite-confirm-btn" disabled={savingFav} onClick={saveFavorite} style={{ padding: "6px 12px", background: "#F59E0B", color: "#0F172A", border: "none", borderRadius: 4, cursor: "pointer", fontSize: 11, fontWeight: 700 }}>
              {savingFav ? "..." : "⭐ Sauver"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
